import axios from 'axios';
import { store } from '@/store/createStore';
import { getCookie, removeCookie } from '@/utils';
import { setGlobalErrors } from '@/store/globalErrors/globalErrors.actions';

// Use REACT_APP_API_URL from environment, fallback to relative /api for local dev
const baseURL = process.env.REACT_APP_API_URL || '/api';

const http = axios.create({
  baseURL,
});

http.interceptors.request.use(
  (request) => {
    const state = store.getState();
    const { token, organizationId } = state.authentication;
    // Same source as useRequest.tsx: the `locale` cookie is written by
    // react-intl-universal (AppIntlLoader) as the UI locale changes, so
    // requests get translated server-side content matching what's on screen.
    // Default to 'fr' (the app's default locale for new users).
    const locale = getCookie('locale') || 'fr';

    if (token) {
      request.headers.common['x-access-token'] = token;
    }
    if (organizationId) {
      request.headers.common['organization-id'] = organizationId;
    }
    if (locale) {
      request.headers.common['Accept-Language'] = locale;
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Clears the auth cookies used to rehydrate `state.authentication` on boot
 * (see `authentication.reducer`'s `initialState` — it reads them straight
 * off cookies). Mirrors `useAuthActions().setLogout` in
 * `hooks/state/authentication.tsx` (the modern `useApiRequest` stack) so the
 * legacy and modern axios stacks log out identically — keep both in sync.
 */
function removeAuthenticationCookies() {
  removeCookie('token');
  removeCookie('organization_id');
  removeCookie('tenant_id');
  removeCookie('authenticated_user_id');
  removeCookie('locale');
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;

      // Dispatches into the SAME `globalErrors` Redux slice the modern
      // `useApiRequest` stack uses (hooks/useRequest.tsx) — the already
      // mounted <GlobalErrors/> (rendered once in App.tsx) reacts to this
      // state and shows the toast, so there's no separate/divergent copy
      // of the toast message here.
      if (status >= 500) {
        store.dispatch(setGlobalErrors({ something_wrong: true }));
      }
      if (status === 401) {
        store.dispatch(setGlobalErrors({ session_expired: true }));

        // Log out — but only if we're not already on an /auth* page. There
        // is no authenticated session to drop there (login/reset-password
        // are called unauthenticated), and reloading on top of it would
        // just interrupt whatever the user is doing for no reason. This is
        // also what prevents a redirect loop: EnsureAuthenticated only
        // redirects to /auth/login for *private* routes, so as long as we
        // don't force a reload from an /auth* page itself, there is no
        // cycle to begin with.
        const onAuthPage = window.location.pathname.startsWith('/auth');
        if (!onAuthPage) {
          removeAuthenticationCookies();
          window.location.reload();
        }
      }
    } else {
      // Network error or request was made but no response received
      console.error('Network error or no response:', error.message);
    }
    return Promise.reject(error);
  },
);

export default http;
